import axios from 'axios';
import * as XLSX from 'xlsx';
import api from './api';
import { INVENTORY_IMPORT_HEADERS } from '../utils/inventoryImportTemplate';
import type { InventoryItemType } from '../types/venue';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ImportAction = 'CREATE' | 'UPDATE' | 'ERROR';
export type CommitStatus = 'SUCCESS' | 'ERROR' | 'SKIPPED';

export interface InventoryImportPreviewRow {
  rowNumber: number;
  action: ImportAction;
  warnings: string[];
  errors: string[];
  resolved: {
    wineId: string | null;
    inventoryDisplayName: string | null;
    supplierId: string | null;
    supplierAction: 'LINK' | 'AUTO_CREATE' | null;
    locationId: string | null;
    willUseDefaultLocation: boolean;
    menuId: string | null;
    /** HOTFIX-UI-3 (fix 1) — resolved Menu Section (null = unsectioned). */
    sectionId: string | null;
    /** The named section doesn't exist yet; commit creates it. */
    sectionWillBeCreated: boolean;
    existingItemId: string | null;
  };
  data: {
    itemType: InventoryItemType | null;
    itemName: string | null;
    sizeValue: number | null;
    sizeUnit: string | null;
    supplierName: string | null;
    menuName: string | null;
    /** HOTFIX-UI-3 (fix 1) — optional section name within the placement menu. */
    menuSection: string | null;
  };
}

export interface InventoryImportPreviewResponse {
  totalRows: number;
  summary: { create: number; update: number; error: number };
  rows: InventoryImportPreviewRow[];
}

export interface InventoryImportCommitRow {
  rowNumber: number;
  action: ImportAction;
  status: CommitStatus;
  errors: string[];
  warnings: string[];
  result: {
    inventoryItemId: string | null;
    supplierCreated: boolean;
    supplierLinked: boolean;
    menuLinked: boolean;
    /** HOTFIX-UI-3 (fix 1) — a Menu Section was created for this row. */
    sectionCreated: boolean;
  };
}

export interface InventoryImportCommitResponse {
  totalRows: number;
  summary: { created: number; updated: number; errored: number };
  rows: InventoryImportCommitRow[];
}

// ── Export row type (matches backend InventoryExportRow) ──────────────────────

export interface InventoryExportRow {
  // Identifiers — authoritative Mongo _id + human/QR-friendly shortId.
  inventoryItemId: string;
  shortId: string | null;
  itemType: InventoryItemType;
  itemName: string;
  /** Resolved user-definable category name; '' when unassigned. */
  category: string;
  /** Resolved user-definable subcategory name; '' when unassigned. */
  subcategory: string;
  sizeValue: number | null;
  sizeUnit: string | null;
  active: boolean;
  binLocation: string | null;
  parLevel: number | null;
  reorderPoint: number | null;
  supplierName: string | null;
  supplierItemCode: string | null;
  cost: number | null;
  /**
   * Menu membership name (generic Menu container, any type), surfaced under the
   * "Menu Name" column on export. Matches the backend InventoryExportRow field.
   */
  menuName: string | null;
  /**
   * PUBLIC-FIXES.1 — the placement's section name, surfaced under "Menu
   * Section" so export → edit → re-import reproduces the same placement.
   */
  menuSection: string | null;
  sellingPrice: number | null;
  lotTracked: boolean;
}

// ── Error extraction ──────────────────────────────────────────────────────────

const IMPORT_ERROR_CODES: Record<string, string> = {
  IMPORT_NO_FILE: 'No file uploaded.',
  IMPORT_INVALID_FILE_TYPE: 'Unsupported file type — upload a .xlsx or .csv file.',
  IMPORT_PARSE_ERROR: 'Could not parse the file. Check it is a valid Excel file.',
  IMPORT_EMPTY_FILE: 'The uploaded file contains no data rows.',
  IMPORT_ROW_LIMIT_EXCEEDED: 'File exceeds the 500-row limit — split and import in batches.',
  IMPORT_MISSING_HEADERS: 'File is missing required column headers.',
};

export function extractInventoryImportError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as Record<string, unknown> | undefined;
    const errObj = data?.['error'] as Record<string, unknown> | undefined;
    const code = errObj?.['code'];
    if (typeof code === 'string' && IMPORT_ERROR_CODES[code]) {
      const missing = errObj?.['missing'];
      if (code === 'IMPORT_MISSING_HEADERS' && Array.isArray(missing)) {
        return `Missing columns: ${(missing as string[]).join(', ')}`;
      }
      return IMPORT_ERROR_CODES[code];
    }
    const msg = errObj?.['message'] ?? data?.['message'];
    if (typeof msg === 'string' && msg) return msg;
    if (err.response?.status === 413) return 'File is too large (max 10 MB).';
    if (err.response?.status === 403) return 'You do not have permission to import.';
  }
  if (err instanceof Error && err.message) return err.message;
  return 'An unexpected error occurred. Please try again.';
}

// ── Existing-items export ─────────────────────────────────────────────────────

/**
 * RECIPE-TYPE.R6 — sheet value for an item type.
 *
 * Was a WINE/MANUFACTURED/else ternary, so every other type — including the new
 * RECIPE — was written as `purchased`. That silently converted a recipe item on
 * download→re-upload. Lower-cased from the stored value so the mapping cannot
 * drift from the type union again, matching the importer's own casing rules.
 */
export function itemTypeSheetValue(itemType: InventoryItemType): string {
  return itemType.toLowerCase();
}

export function downloadExistingItemsAsImport(rows: InventoryExportRow[]): void {
  const sheetRows = rows.map(row => ({
    'Inventory Item ID':  row.inventoryItemId,
    'Short ID':           row.shortId ?? '',
    'Item Type':          itemTypeSheetValue(row.itemType),
    'Item Name':          row.itemName,
    'Category':           row.category ?? '',
    'Subcategory':        row.subcategory ?? '',
    'Size Value':         row.sizeValue ?? '',
    'Size Unit':          row.sizeUnit ?? '',
    'Active':             row.active ? 'true' : 'false',
    'Bin Location':       row.binLocation ?? '',
    'Par Level':          row.parLevel ?? '',
    'Reorder Point':      row.reorderPoint ?? '',
    'Location':           '',
    'Supplier Name':      row.supplierName ?? '',
    'Supplier Item Code': row.supplierItemCode ?? '',
    'Cost':               row.cost ?? '',
    // Sole placement column (generic Menu container, any type). Holds the Menu
    // membership name, or blank when the item is not placed on a menu.
    'Menu Name':          row.menuName ?? '',
    'Menu Section':       row.menuSection ?? '',
    'Selling Price':      row.sellingPrice ?? '',
    'Lot Tracked':        row.lotTracked ? 'TRUE' : 'FALSE',
  }));

  const ws = XLSX.utils.json_to_sheet(sheetRows, { header: INVENTORY_IMPORT_HEADERS });
  ws['!cols'] = INVENTORY_IMPORT_HEADERS.map(h =>
    ({ wch: Math.max(h.length + 2, 14) })
  );
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Inventory Items');
  XLSX.writeFile(wb, 'inventory-items-export.xlsx');
}

// ── Import / export service ───────────────────────────────────────────────────

const importBase = (venueId: string) => `/admin/venue/${venueId}/inventory/import`;
const exportBase = (venueId: string) => `/admin/venue/${venueId}/inventory/export`;

const inventoryImportService = {
  /**
   * IMPORT-DESTINATION.1 — `destinationLocationId` is where rows that name no
   * location land. The sheet's "Opening Location" column still wins per row, so
   * a mixed delivery can still be split; this answers the ordinary case of one
   * import going to one place.
   */
  preview: async (
    venueId: string,
    file: File,
    destinationLocationId?: string | null,
  ): Promise<InventoryImportPreviewResponse> => {
    const fd = new FormData();
    fd.append('file', file);
    if (destinationLocationId) fd.append('destinationLocationId', destinationLocationId);
    // Content-Type must be absent so the browser sets multipart/form-data;boundary=…
    // The shared api instance defaults to application/json which breaks multer parsing.
    const res = await api.post(`${importBase(venueId)}/preview`, fd, {
      headers: { 'Content-Type': undefined },
    });
    return res.data as InventoryImportPreviewResponse;
  },

  commit: async (
    venueId: string,
    file: File,
    destinationLocationId?: string | null,
  ): Promise<InventoryImportCommitResponse> => {
    const fd = new FormData();
    fd.append('file', file);
    if (destinationLocationId) fd.append('destinationLocationId', destinationLocationId);
    const res = await api.post(`${importBase(venueId)}/commit`, fd, {
      headers: { 'Content-Type': undefined },
    });
    return res.data as InventoryImportCommitResponse;
  },

  exportItems: async (venueId: string): Promise<InventoryExportRow[]> => {
    const res = await api.get(exportBase(venueId));
    return res.data as InventoryExportRow[];
  },
};

export default inventoryImportService;
